import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  TextField
} from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DownloadIcon from '@mui/icons-material/Download';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { escapeHtml } from '../utils/escapeHtml';
import { api } from '../services/AuthService';
import { useAuth } from '../services/AuthService';
import { OperationalSummary } from './OperationsCenter';

function Dashboard() {
  const [stats, setStats] = useState({
    totalLanchonetes: 0,
    totalProdutos: 0,
    totalPedidos: 0,
    totalUsuarios: 0,
    totalVendas: 0,
    pedidosHoje: 0
  });
  const [pedidosRecentes, setPedidosRecentes] = useState([]);
  const [vendasMensais, setVendasMensais] = useState([]);
  const [motoboyStats, setMotoboyStats] = useState([]);
  const [reportDate, setReportDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dailyReport, setDailyReport] = useState(null);
  const [deliveryCallReport, setDeliveryCallReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loading, setLoading] = useState(true);
  const { isAdminMaster, getLanchoneteId } = useAuth();

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      let pedidosResponse;
      
      if (isAdminMaster()) {
        // Admin Master usa estatísticas gerais
        const statsResponse = await api.get('/pedidos/stats/all');
        pedidosResponse = await api.get('/pedidos?limit=10');
        // Admin Master vê todas as estatísticas
        const lanchonetesResponse = await api.get('/lanchonetes');
        const usuariosResponse = await api.get('/users');
        
        // Carregar estatísticas de motoboys para a planilha
        const motoboysResponse = await api.get('/motoboys');
        const motoboys = motoboysResponse.data.filter(m => m.motoboy_scope === 'APP');
        
        const mStats = await Promise.all(motoboys.map(async (m) => {
          const deliveriesResponse = await api.get(`/motoboys/${m.id}/deliveries-by-date`);
          const deliveries = deliveriesResponse.data;
          
          // Agrupar entregas por lanchonete para mostrar na planilha
          const lanchonetesMap = {};
          deliveries.forEach(d => {
            lanchonetesMap[d.lanchonete_name] = (lanchonetesMap[d.lanchonete_name] || 0) + 1;
          });

          return {
            id: m.id,
            name: m.name,
            total_deliveries: deliveries.length,
            total_earnings: deliveries.reduce((acc, d) => acc + Number(d.delivery_fee || 0), 0),
            lanchonetes_atendidas: Object.entries(lanchonetesMap)
              .map(([name, count]) => `${name} (${count})`)
              .join(', ')
          };
        }));
        setMotoboyStats(mStats);

        setStats({
          totalLanchonetes: lanchonetesResponse.data.length,
          totalProdutos: 0,
          totalPedidos: statsResponse.data.total_pedidos,
          totalUsuarios: usuariosResponse.data.length,
          totalVendas: statsResponse.data.total_vendas,
          pedidosHoje: statsResponse.data.pedidos_hoje ?? 0
        });
      } else {
        // Admin Lanchonete usa estatísticas específicas
        const lanchoneteId = getLanchoneteId();
        pedidosResponse = { data: [] };
        
        if (lanchoneteId) {
          pedidosResponse = await api.get(`/pedidos?lanchonete_id=${lanchoneteId}&limit=10`);
          const lanchoneteStats = await api.get(`/lanchonetes/${lanchoneteId}/stats`);

          setStats({
            totalLanchonetes: 1,
            totalProdutos: 0,
            totalPedidos: lanchoneteStats.data.total_pedidos,
            totalUsuarios: lanchoneteStats.data.total_clientes,
            totalVendas: lanchoneteStats.data.total_vendas,
            pedidosHoje: lanchoneteStats.data.pedidos_hoje ?? 0
          });
        } else {
          // Admin Lanchonete sem lanchonete_id
          setStats({
            totalLanchonetes: 0,
            totalProdutos: 0,
            totalPedidos: 0,
            totalUsuarios: 0,
            totalVendas: 0,
            pedidosHoje: 0
          });
        }
      }
      
      setPedidosRecentes(pedidosResponse.data);
      
      // Dados simulados para gráficos (em produção viriam da API)
      setVendasMensais([
        { month: 'Jan', vendas: 4000 },
        { month: 'Fev', vendas: 3000 },
        { month: 'Mar', vendas: 5000 },
        { month: 'Abr', vendas: 4500 },
        { month: 'Mai', vendas: 6000 },
        { month: 'Jun', vendas: 5500 },
      ]);
      
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdminMaster, getLanchoneteId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const loadDailyReport = useCallback(async () => {
    if (isAdminMaster()) return;
    if (!getLanchoneteId()) return;

    try {
      setLoadingReport(true);
      const params = new URLSearchParams();
      if (reportDate) params.set('date', reportDate);
      const response = await api.get(`/pedidos/report/daily?${params.toString()}`);
      setDailyReport(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatório diário:', error);
      setDailyReport(null);
    } finally {
      setLoadingReport(false);
    }
  }, [getLanchoneteId, isAdminMaster, reportDate]);

  useEffect(() => {
    if (!isAdminMaster() && getLanchoneteId()) {
      loadDailyReport();
    }
  }, [isAdminMaster, getLanchoneteId, loadDailyReport]);

  const loadDeliveryCallReport = useCallback(async () => {
    if (isAdminMaster() || !getLanchoneteId()) return;

    try {
      const response = await api.get('/delivery-calls/report');
      setDeliveryCallReport(response.data);
    } catch (error) {
      console.error('Erro ao carregar valores dos chamados de motoboy:', error);
    }
  }, [getLanchoneteId, isAdminMaster]);

  useEffect(() => {
    if (isAdminMaster() || !getLanchoneteId()) return undefined;

    loadDeliveryCallReport();
    const interval = window.setInterval(() => {
      if (!document.hidden) loadDeliveryCallReport();
    }, 30000);

    const refreshWhenVisible = () => {
      if (!document.hidden) loadDeliveryCallReport();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [getLanchoneteId, isAdminMaster, loadDeliveryCallReport]);

  const handlePrintCashReport = () => {
    if (!dailyReport) return;

    const w = window.open('', '_blank');
    if (!w) return;

    const brl = (value) =>
      `R$ ${Number(value || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;

    const html = `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Faturamento do Caixa</title>
          <style>
            * { color: #000000 !important; font-weight: 900 !important; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 5px; width: 240px; margin: 0 auto; }
            h1 { margin: 0 0 5px; font-size: 14px; text-transform: uppercase; }
            h2 { margin: 0 0 10px; font-size: 11px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 2px solid #000; padding: 6px; text-align: left; font-size: 11px; }
            th { background: #fff; }
          </style>
        </head>
        <body>
          <h1>Faturamento do Caixa</h1>
          <h2>Data: ${escapeHtml(reportDate)}</h2>
          <table>
            <tr><th>Indicador</th><th>Valor</th></tr>
            <tr><td>Total de pedidos</td><td>${dailyReport.total_pedidos ?? 0}</td></tr>
            <tr><td>Faturamento total do dia</td><td>${brl(dailyReport.total_valor)}</td></tr>
            <tr><td>Pedidos (dia 6h-18h)</td><td>${dailyReport.pedidos_dia_periodo ?? 0}</td></tr>
            <tr><td>Faturamento (dia 6h-18h)</td><td>${brl(dailyReport.valor_dia_periodo)}</td></tr>
            <tr><td>Pedidos (noite 18h-6h)</td><td>${dailyReport.pedidos_noite ?? 0}</td></tr>
            <tr><td>Faturamento (noite 18h-6h)</td><td>${brl(dailyReport.valor_noite)}</td></tr>
          </table>
          <script>window.onload = function () { window.print(); };</script>
        </body>
      </html>`;

    w.document.write(html);
    w.document.close();
  };

  const StatCard = ({ title, value, icon, color }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center">
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: 1,
              p: 1,
              mr: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="overline">
              {title}
            </Typography>
            <Typography variant="h6" component="div">
              {value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {!isAdminMaster() && <OperationalSummary compact />}
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Dashboard
      </Typography>
      <Typography variant="body1" color="textSecondary" gutterBottom>
        {isAdminMaster() ? 'Visão geral do sistema' : 'Visão geral da sua lanchonete'}
      </Typography>

      {/* Cards de estatísticas */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Lanchonetes"
            value={stats.totalLanchonetes}
            icon={<StoreIcon />}
            color="#E50914"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Produtos"
            value={stats.totalProdutos}
            icon={<ShoppingCartIcon />}
            color="#2196F3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pedidos"
            value={stats.totalPedidos}
            icon={<ReceiptIcon />}
            color="#4CAF50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Clientes"
            value={stats.totalUsuarios}
            icon={<PeopleIcon />}
            color="#9C27B0"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Vendas Totais"
            value={new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(Number(stats.totalVendas) || 0)}
            icon={<AttachMoneyIcon />}
            color="#FF9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pedidos Hoje"
            value={stats.pedidosHoje}
            icon={<TrendingUpIcon />}
            color="#F44336"
          />
        </Grid>
      </Grid>

      {!isAdminMaster() && deliveryCallReport && (() => {
        const deliveredCalls = (deliveryCallReport.entregas || []).filter(
          (call) => String(call.status || '').toLowerCase() === 'delivered'
        );
        const generatedAmount = deliveredCalls.reduce(
          (sum, call) => sum + Number(call.courier_earning || 0),
          0
        );
        const pendingAmount = Number(deliveryCallReport.total_a_pagar || 0);
        const paidAmount = Math.max(0, generatedAmount - pendingAmount);
        const formatMoney = (value) => new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(Number(value) || 0);

        return (
          <Paper variant="outlined" sx={{ p: 2.5, mt: 3, borderRadius: 3, bgcolor: '#f0fdf4', borderColor: '#bbf7d0' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Box>
                <Typography variant="h6" fontWeight="bold" color="success.dark">
                  Valores dos chamados de motoboy
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Corridas avulsas chamadas pela lanchonete, inclusive entregas recebidas pelo WhatsApp.
                </Typography>
              </Box>
              <Button size="small" variant="outlined" color="success" onClick={loadDeliveryCallReport}>
                Atualizar valores
              </Button>
            </Box>

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Corridas aceitas</Typography>
                <Typography variant="h6" fontWeight="bold">{deliveryCallReport.total_corridas || 0}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Entregas concluídas</Typography>
                <Typography variant="h6" fontWeight="bold">{deliveryCallReport.corridas_entregues || 0}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Já pago</Typography>
                <Typography variant="h6" fontWeight="bold" color="success.dark">{formatMoney(paidAmount)}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Falta pagar</Typography>
                <Typography variant="h6" fontWeight="bold" color={pendingAmount > 0 ? 'warning.dark' : 'success.dark'}>
                  {formatMoney(pendingAmount)}
                </Typography>
              </Grid>
            </Grid>

            {deliveryCallReport.por_motoboy?.length > 0 && (
              <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
                {deliveryCallReport.por_motoboy.map((motoboy) => (
                  <Chip
                    key={motoboy.motoboy_id}
                    color={Number(motoboy.total_a_receber || 0) > 0 ? 'warning' : 'success'}
                    variant="outlined"
                    label={`${motoboy.motoboy_name}: ${motoboy.entregues} entrega(s) - falta pagar ${formatMoney(motoboy.total_a_receber)}`}
                  />
                ))}
              </Box>
            )}
          </Paper>
        );
      })()}

      {/* Gráficos */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Vendas Mensais
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={vendasMensais}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="vendas" 
                  stroke="#E50914" 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Status dos Pedidos
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                { status: 'Pendentes', value: 12 },
                { status: 'Aceitos', value: 8 },
                { status: 'Entregues', value: 45 },
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="status" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#E50914" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Pedidos recentes */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Pedidos Recentes
            </Typography>
            {pedidosRecentes.length === 0 ? (
              <Typography color="textSecondary">
                Nenhum pedido encontrado
              </Typography>
            ) : (
              pedidosRecentes.map((pedido) => (
                <Box
                  key={pedido.id}
                  sx={{
                    p: 2,
                    mb: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="subtitle2">
                        Pedido #{pedido.id}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {pedido.customer_name || 'Cliente'} - {pedido.lanchonete_name}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="subtitle2" color="primary">
                        R$ {pedido.total_amount?.toFixed(2) || '0.00'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {new Date(pedido.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Quadro de entregas dos motoboys (admin master) posicionado abaixo */}
      {false && isAdminMaster() && (
        <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Entregas dos Motoboys (APP)
                </Typography>
                <Button 
                  startIcon={<DownloadIcon />} 
                  size="small" 
                  variant="outlined"
                  onClick={() => {
                    const csv = "Motoboy,Entregas,Ganhos (R$)\n" + 
                      motoboyStats.map(m => `${m.name},${m.total_deliveries},${m.total_earnings.toFixed(2)}`).join("\n");
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.setAttribute('hidden', '');
                    a.setAttribute('href', url);
                    a.setAttribute('download', `entregas_motoboys_${new Date().toISOString().split('T')[0]}.csv`);
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }}
                >
                  Exportar CSV
                </Button>
              </Box>
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Motoboy</TableCell>
                      <TableCell>Lanchonetes (Qtd)</TableCell>
                      <TableCell align="center">Entregas</TableCell>
                      <TableCell align="right">Ganhos (R$)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {motoboyStats.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">Nenhum dado disponível</TableCell>
                      </TableRow>
                    ) : (
                      motoboyStats.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {m.name}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', maxWidth: 280 }}>
                            {m.lanchonetes_atendidas || '---'}
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={m.total_deliveries} size="small" color="primary" />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            R$ {m.total_earnings.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {!isAdminMaster() && (
        <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Faturamento do Caixa (Dia)
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                  <TextField
                    type="date"
                    size="small"
                    label="Data"
                    InputLabelProps={{ shrink: true }}
                    value={reportDate}
                    onChange={(e) => setReportDate(e.target.value)}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={loadDailyReport}
                    disabled={loadingReport}
                  >
                    {loadingReport ? 'Atualizando...' : 'Atualizar'}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handlePrintCashReport}
                    disabled={!dailyReport}
                    startIcon={<DownloadIcon />}
                  >
                    Imprimir
                  </Button>
                </Box>
              </Box>

              {dailyReport ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Pedidos no dia</Typography>
                      <Typography variant="h5">{dailyReport.total_pedidos ?? 0}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Faturamento do dia</Typography>
                      <Typography variant="h5">
                        R$ {Number(dailyReport.total_valor ?? 0).toFixed(2)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Dia 6h-18h</Typography>
                      <Typography variant="h6">
                        {dailyReport.pedidos_dia_periodo ?? 0} - R$ {Number(dailyReport.valor_dia_periodo ?? 0).toFixed(2)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Noite 18h-6h</Typography>
                      <Typography variant="h6">
                        {dailyReport.pedidos_noite ?? 0} - R$ {Number(dailyReport.valor_noite ?? 0).toFixed(2)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Selecione uma data e clique em Atualizar para ver o faturamento do dia.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default Dashboard;
